/**
 * Cliente das páginas de Game Design.
 *
 * <p>Duas chamadas: ler a página e gravá-la inteira. O PUT manda o documento todo
 * porque a edição aqui é de estrutura — apagar uma coluna é a ausência dela no que
 * chega, e um PATCH precisaria de uma linguagem para dizer "esta some".
 */
import { api } from '../../api/cliente';

const BASE = '/api/projetos/mistik-island/game-design/paginas';

export interface Pagina {
  chave: string;
  /**
   * Quantas vezes cada quadro foi gravado. Quadro ausente vale zero.
   *
   * <p>A tela devolve este número ao gravar; é assim que o servidor percebe que alguém
   * escreveu no meio e recusa em vez de sobrescrever.
   */
  versoes?: Record<string, number>;
  /**
   * Opaco de propósito, dos dois lados: o backend guarda o documento sem conhecer a
   * forma das tabelas, e quem lê aqui passa por `combinar`, que confere o formato antes
   * de a tela desenhar qualquer coisa.
   */
  conteudo: unknown;
  atualizadoPor?: string;
  atualizadoEm?: string;
}

export const chaveDaPagina = (chave: string) => ['game-design', 'pagina', chave] as const;

export function ler(chave: string): Promise<Pagina> {
  return api<Pagina>(`${BASE}/${chave}`);
}

export function salvar(chave: string, conteudo: unknown): Promise<Pagina> {
  return api<Pagina>(`${BASE}/${chave}`, {
    method: 'PUT',
    body: JSON.stringify({ conteudo }),
  });
}

/**
 * Grava <b>um quadro</b>, sem tocar nos outros.
 *
 * <p>É o que impede duas pessoas na mesma página de apagarem o trabalho uma da outra: o
 * PUT acima manda o documento inteiro, montado do snapshot que o navegador carregou, e
 * quem gravasse por último levava junto os quadros como estavam quando abriu.
 *
 * @param versaoBase a versão que esta tela leu. Nula só quando o quadro nunca foi
 *        gravado; com um número, o servidor devolve 409 se alguém gravou no meio.
 */
/** Uma linha do log de alterações. Sem o conteúdo — ver `AlteracaoDTO` no backend. */
export interface Alteracao {
  id: number;
  quadro: string;
  versao: number;
  autor?: string;
  criadoEm: string;
}

export const chaveDoHistorico = (chave: string) =>
  ['game-design', 'pagina', chave, 'historico'] as const;

export function historico(chave: string): Promise<Alteracao[]> {
  return api<Alteracao[]>(`${BASE}/${chave}/historico`);
}

/**
 * Devolve um quadro ao conteúdo de uma gravação anterior.
 *
 * <p>`versaoBase` é a mesma trava da gravação comum: restaurar por cima do trabalho de
 * outra pessoa é a mesma perda, e mais fácil de cometer sem perceber — quem clica está
 * olhando para uma lista de datas, não para o texto.
 */
export function restaurar(
  chave: string,
  versaoId: number,
  versaoBase: number | null
): Promise<Pagina> {
  return api<Pagina>(`${BASE}/${chave}/historico/${versaoId}/restaurar`, {
    method: 'POST',
    body: JSON.stringify({ versaoBase }),
  });
}

export function salvarQuadro(
  chave: string,
  quadro: string,
  conteudo: unknown,
  versaoBase: number | null
): Promise<Pagina> {
  return api<Pagina>(`${BASE}/${chave}/quadros/${quadro}`, {
    method: 'PATCH',
    body: JSON.stringify({ conteudo, versaoBase }),
  });
}
